import React, { useState } from 'react';
import ShowcasePageWrapper from '../shared/ShowcasePageWrapper';
import { SLDSLineChart, SLDSDoughnutChart, MiniDonut, SLDS_CHART_COLORS } from '../shared/ChartComponents';
import Button from '@salesforce/design-system-react/components/button';
import ButtonGroup from '@salesforce/design-system-react/components/button-group';
import Icon from '@salesforce/design-system-react/components/icon';
import Avatar from '@salesforce/design-system-react/components/avatar';
import Tabs from '@salesforce/design-system-react/components/tabs';
import TabsPanel from '@salesforce/design-system-react/components/tabs/panel';
import DataTable from '@salesforce/design-system-react/components/data-table';
import DataTableColumn from '@salesforce/design-system-react/components/data-table/column';
import DataTableCell from '@salesforce/design-system-react/components/data-table/cell';
import ScopedNotification from '@salesforce/design-system-react/components/scoped-notification';
import ExpandableSection from '@salesforce/design-system-react/components/expandable-section';

// ---------------------------------------------------------------------------
// ComponentCard - Blue border card with floating label
// ---------------------------------------------------------------------------
const ComponentCard = ({ title, children, label }) => (
  <div style={{
    backgroundColor: 'white',
    border: '2px solid #0176d3',
    borderRadius: '4px',
    padding: '20px',
    marginBottom: '24px',
    position: 'relative',
  }}>
    {label && (
      <div style={{
        position: 'absolute',
        top: '-12px',
        left: '16px',
        backgroundColor: '#0176d3',
        color: 'white',
        padding: '4px 12px',
        borderRadius: '4px',
        fontSize: '11px',
        fontWeight: '700',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
      }}>
        {label}
      </div>
    )}
    <h3 style={{
      fontSize: '16px',
      fontWeight: '700',
      marginBottom: '16px',
      marginTop: label ? '8px' : '0',
    }}>
      {title}
    </h3>
    {children}
  </div>
);

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const fieldLabelStyle = {
  fontSize: '12px',
  color: '#706e6b',
  fontWeight: '400',
  marginBottom: '4px',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

const fieldValueStyle = {
  fontSize: '14px',
  color: '#080707',
  fontWeight: '400',
  marginBottom: '16px',
};

const badgeColors = {
  'Closed Won': { bg: '#2e844a', text: 'white' },
  'Negotiation': { bg: '#fe9339', text: 'white' },
  'Proposal': { bg: '#0176d3', text: 'white' },
  'Qualification': { bg: '#9050e9', text: 'white' },
  'Prospecting': { bg: '#706e6b', text: 'white' },
  'High': { bg: '#c23934', text: 'white' },
  'Medium': { bg: '#fe9339', text: 'white' },
  'Low': { bg: '#2e844a', text: 'white' },
  'Critical': { bg: '#c23934', text: 'white' },
  'New': { bg: '#0176d3', text: 'white' },
  'Working': { bg: '#fe9339', text: 'white' },
  'Escalated': { bg: '#c23934', text: 'white' },
  'Closed': { bg: '#2e844a', text: 'white' },
  'Open': { bg: '#0176d3', text: 'white' },
};

const StatusBadge = ({ status }) => {
  const colors = badgeColors[status] || { bg: '#706e6b', text: 'white' };
  return (
    <span style={{
      display: 'inline-block',
      backgroundColor: colors.bg,
      color: colors.text,
      padding: '2px 8px',
      borderRadius: '12px',
      fontSize: '11px',
      fontWeight: '600',
      lineHeight: '18px',
    }}>
      {status}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Stage Path Component
// ---------------------------------------------------------------------------
const StagePath = ({ stages, currentStage }) => {
  const currentIndex = stages.indexOf(currentStage);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0', marginTop: '12px' }}>
      {stages.map((stage, idx) => {
        const isComplete = idx < currentIndex;
        const isCurrent = idx === currentIndex;
        const bgColor = isComplete ? '#2e844a' : isCurrent ? '#0176d3' : '#dddbda';
        const textColor = isComplete || isCurrent ? 'white' : '#706e6b';
        return (
          <div key={stage} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <div style={{
              flex: 1,
              backgroundColor: bgColor,
              color: textColor,
              padding: '8px 16px',
              fontSize: '12px',
              fontWeight: '600',
              textAlign: 'center',
              borderRadius: idx === 0 ? '4px 0 0 4px' : idx === stages.length - 1 ? '0 4px 4px 0' : '0',
              borderRight: idx < stages.length - 1 ? '2px solid white' : 'none',
              position: 'relative',
            }}>
              {isComplete && (
                <Icon category="utility" name="check" size="xx-small" style={{ fill: 'white', marginRight: '4px' }} />
              )}
              {stage}
              {isCurrent && (
                <div style={{
                  position: 'absolute',
                  bottom: '-6px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '0',
                  height: '0',
                  borderLeft: '6px solid transparent',
                  borderRight: '6px solid transparent',
                  borderTop: `6px solid ${bgColor}`,
                }} />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Navigation Bar
// ---------------------------------------------------------------------------
const NavBar = ({ activeTab, onTabChange }) => {
  const tabs = ['Record Home', 'Details', 'Related', 'Activity'];
  return (
    <div style={{
      backgroundColor: 'white',
      border: '1px solid #dddbda',
      borderRadius: '4px',
      marginBottom: '16px',
      display: 'flex',
      alignItems: 'center',
    }}>
      {tabs.map((tab) => (
        <button
          key={tab}
          onClick={() => onTabChange(tab)}
          style={{
            padding: '12px 24px',
            fontSize: '14px',
            fontWeight: activeTab === tab ? '700' : '400',
            color: activeTab === tab ? '#0176d3' : '#3e3e3c',
            backgroundColor: 'transparent',
            border: 'none',
            borderBottom: activeTab === tab ? '3px solid #0176d3' : '3px solid transparent',
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
        >
          {tab}
        </button>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Timeline Activity Item
// ---------------------------------------------------------------------------
const TimelineItem = ({ icon, iconColor, iconBg, title, description, timestamp, isLast }) => (
  <div style={{ display: 'flex', gap: '12px', position: 'relative' }}>
    {/* Timeline line */}
    {!isLast && (
      <div style={{
        position: 'absolute',
        left: '15px',
        top: '32px',
        bottom: '-12px',
        width: '2px',
        backgroundColor: '#dddbda',
      }} />
    )}
    {/* Icon dot */}
    <div style={{
      width: '32px',
      height: '32px',
      borderRadius: '50%',
      backgroundColor: iconBg || '#f3f3f3',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      zIndex: 1,
    }}>
      <Icon category="utility" name={icon} size="x-small" style={{ fill: iconColor || '#706e6b' }} />
    </div>
    {/* Content */}
    <div style={{ flex: 1, paddingBottom: isLast ? '0' : '20px' }}>
      <div style={{ fontSize: '14px', fontWeight: '600', color: '#080707', marginBottom: '4px' }}>
        {title}
      </div>
      <div style={{ fontSize: '13px', color: '#3e3e3c', marginBottom: '4px' }}>
        {description}
      </div>
      <div style={{ fontSize: '12px', color: '#706e6b' }}>
        {timestamp}
      </div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Quick Action Button
// ---------------------------------------------------------------------------
const QuickActionButton = ({ icon, label, onClick }) => (
  <button
    onClick={onClick}
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '8px',
      padding: '12px 8px',
      backgroundColor: 'transparent',
      border: '1px solid #dddbda',
      borderRadius: '4px',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
      width: '100%',
    }}
    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f3f3f3'; }}
    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
  >
    <Icon category="utility" name={icon} size="small" style={{ fill: '#0176d3' }} />
    <span style={{ fontSize: '11px', color: '#3e3e3c', fontWeight: '500', textAlign: 'center', lineHeight: '1.3' }}>
      {label}
    </span>
  </button>
);

// ---------------------------------------------------------------------------
// Contact Card
// ---------------------------------------------------------------------------
const ContactCard = ({ name, title, email, initials }) => (
  <div style={{
    backgroundColor: 'white',
    border: '1px solid #dddbda',
    borderRadius: '4px',
    padding: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  }}>
    <Avatar
      variant="user"
      label={name}
      size="medium"
    />
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: '14px', fontWeight: '600', color: '#0176d3', marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {name}
      </div>
      <div style={{ fontSize: '12px', color: '#3e3e3c', marginBottom: '2px' }}>{title}</div>
      <div style={{ fontSize: '12px', color: '#706e6b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{email}</div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Field Display - Label / Value pair
// ---------------------------------------------------------------------------
const FieldDisplay = ({ label, value, isLink }) => (
  <div style={{ marginBottom: '16px' }}>
    <div style={fieldLabelStyle}>{label}</div>
    <div style={{
      ...fieldValueStyle,
      color: isLink ? '#0176d3' : '#080707',
      cursor: isLink ? 'pointer' : 'default',
    }}>
      {value}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------
const opportunitiesData = [
  { id: 'opp-1', name: 'Acme Corp - Enterprise Suite', amount: 125000, stage: 'Closed Won', closeDate: '2025-12-15' },
  { id: 'opp-2', name: 'Acme Corp - Analytics Add-on', amount: 45000, stage: 'Negotiation', closeDate: '2026-01-20' },
  { id: 'opp-3', name: 'Acme Corp - Support Package', amount: 32000, stage: 'Proposal', closeDate: '2026-02-10' },
  { id: 'opp-4', name: 'Acme Corp - Pro License Expansion', amount: 78000, stage: 'Qualification', closeDate: '2026-03-01' },
  { id: 'opp-5', name: 'Acme Corp - Data Migration', amount: 55000, stage: 'Prospecting', closeDate: '2026-04-15' },
];

const contactsData = [
  { id: 'con-1', name: 'Sarah Johnson', title: 'Chief Technology Officer', email: 'sarah.johnson@acmecorp.com' },
  { id: 'con-2', name: 'James Chen', title: 'VP of Engineering', email: 'james.chen@acmecorp.com' },
  { id: 'con-3', name: 'Maria Garcia', title: 'Director of Operations', email: 'maria.garcia@acmecorp.com' },
  { id: 'con-4', name: 'Robert Williams', title: 'Procurement Manager', email: 'robert.williams@acmecorp.com' },
];

const casesData = [
  { id: 'case-1', number: '00045123', subject: 'Integration API timeout issues', status: 'Open', priority: 'High' },
  { id: 'case-2', number: '00045098', subject: 'User provisioning request for new team', status: 'Working', priority: 'Medium' },
  { id: 'case-3', number: '00044987', subject: 'Dashboard custom report not loading', status: 'Escalated', priority: 'Critical' },
  { id: 'case-4', number: '00044856', subject: 'License renewal billing question', status: 'Closed', priority: 'Low' },
  { id: 'case-5', number: '00044721', subject: 'SSO configuration assistance', status: 'Closed', priority: 'Medium' },
];

const timelineActivities = [
  {
    icon: 'call',
    iconColor: '#2e844a',
    iconBg: '#ebf7e6',
    title: 'Call with Sarah Johnson',
    description: 'Discussed Q1 renewal terms and expansion opportunity for Analytics Add-on. Sarah confirmed budget approval for $45K.',
    timestamp: 'Today at 2:30 PM - 25 min',
  },
  {
    icon: 'email',
    iconColor: '#0176d3',
    iconBg: '#e6f2fb',
    title: 'Email: Follow-up on Proposal',
    description: 'Sent updated proposal document with revised pricing tiers and implementation timeline to James Chen.',
    timestamp: 'Yesterday at 4:15 PM',
  },
  {
    icon: 'event',
    iconColor: '#9050e9',
    iconBg: '#f0e6fb',
    title: 'Meeting: Quarterly Business Review',
    description: 'QBR with Acme leadership team. Reviewed product usage metrics, discussed roadmap alignment, and identified 3 new expansion areas.',
    timestamp: 'Jan 28, 2026 at 10:00 AM - 1 hr',
  },
  {
    icon: 'task',
    iconColor: '#fe9339',
    iconBg: '#fef4e6',
    title: 'Task Completed: Update CRM Records',
    description: 'Updated all contact roles, added new stakeholder Maria Garcia as Director of Operations.',
    timestamp: 'Jan 25, 2026 at 11:30 AM',
  },
  {
    icon: 'note',
    iconColor: '#706e6b',
    iconBg: '#f3f3f3',
    title: 'Note: Competitive Intelligence',
    description: 'Acme evaluating Competitor X for analytics module. Need to schedule deep-dive demo to show differentiation on real-time dashboards.',
    timestamp: 'Jan 20, 2026 at 3:00 PM',
  },
];

const revenueChartData = {
  labels: ['Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb'],
  datasets: [{
    label: 'Revenue',
    data: [180000, 195000, 210000, 240000, 225000, 260000],
    borderColor: SLDS_CHART_COLORS[0],
    backgroundColor: SLDS_CHART_COLORS[0] + '33',
    tension: 0.3,
    fill: true,
    pointRadius: 3,
    pointBackgroundColor: SLDS_CHART_COLORS[0],
  }],
};

const productMixData = {
  labels: ['Enterprise Suite', 'Analytics', 'Support', 'Training'],
  datasets: [{
    data: [45, 25, 18, 12],
    backgroundColor: [SLDS_CHART_COLORS[0], SLDS_CHART_COLORS[1], SLDS_CHART_COLORS[2], SLDS_CHART_COLORS[4]],
    borderWidth: 2,
    borderColor: 'white',
  }],
};

// ---------------------------------------------------------------------------
// Custom cell renderers for DataTable
// ---------------------------------------------------------------------------
const AmountCell = ({ children, ...props }) => (
  <DataTableCell {...props}>
    <span style={{ fontWeight: '600', color: '#080707' }}>
      ${Number(children).toLocaleString('en-US')}
    </span>
  </DataTableCell>
);
AmountCell.displayName = DataTableCell.displayName;

const StageCell = ({ children, ...props }) => (
  <DataTableCell {...props}>
    <StatusBadge status={children} />
  </DataTableCell>
);
StageCell.displayName = DataTableCell.displayName;

const NameLinkCell = ({ children, ...props }) => (
  <DataTableCell {...props}>
    <span style={{ color: '#0176d3', cursor: 'pointer', fontWeight: '500' }}>
      {children}
    </span>
  </DataTableCell>
);
NameLinkCell.displayName = DataTableCell.displayName;

// ---------------------------------------------------------------------------
// Left Column - Highlights Panel
// ---------------------------------------------------------------------------
const HighlightsPanel = () => (
  <ComponentCard title="Key Account Highlights" label="HIGHLIGHTS PANEL">
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
      {/* Annual Revenue */}
      <div style={{
        backgroundColor: '#f3f3f3',
        borderRadius: '4px',
        padding: '16px',
        textAlign: 'center',
      }}>
        <Icon category="utility" name="money" size="small" style={{ fill: '#2e844a', marginBottom: '8px' }} />
        <div style={{ fontSize: '11px', color: '#706e6b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
          Annual Revenue
        </div>
        <div style={{ fontSize: '24px', fontWeight: '700', color: '#080707' }}>
          $2.4M
        </div>
      </div>

      {/* Health Score */}
      <div style={{
        backgroundColor: '#f3f3f3',
        borderRadius: '4px',
        padding: '16px',
        textAlign: 'center',
      }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '8px' }}>
          <MiniDonut value={87} total={100} color="#2e844a" size={48} />
        </div>
        <div style={{ fontSize: '11px', color: '#706e6b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
          Health Score
        </div>
        <div style={{ fontSize: '24px', fontWeight: '700', color: '#2e844a' }}>
          87%
        </div>
      </div>

      {/* Open Opps */}
      <div style={{
        backgroundColor: '#f3f3f3',
        borderRadius: '4px',
        padding: '16px',
        textAlign: 'center',
      }}>
        <Icon category="utility" name="opportunity" size="small" style={{ fill: '#0176d3', marginBottom: '8px' }} />
        <div style={{ fontSize: '11px', color: '#706e6b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
          Open Opps
        </div>
        <div style={{ fontSize: '24px', fontWeight: '700', color: '#080707' }}>
          5
        </div>
      </div>

      {/* Active Contacts */}
      <div style={{
        backgroundColor: '#f3f3f3',
        borderRadius: '4px',
        padding: '16px',
        textAlign: 'center',
      }}>
        <Icon category="utility" name="people" size="small" style={{ fill: '#9050e9', marginBottom: '8px' }} />
        <div style={{ fontSize: '11px', color: '#706e6b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
          Active Contacts
        </div>
        <div style={{ fontSize: '24px', fontWeight: '700', color: '#080707' }}>
          12
        </div>
      </div>
    </div>

    {/* Account Summary */}
    <div style={{ marginTop: '20px', padding: '12px', backgroundColor: '#f3f3f3', borderRadius: '4px' }}>
      <div style={{ fontSize: '12px', fontWeight: '600', color: '#3e3e3c', marginBottom: '8px' }}>
        Account Summary
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
        <span style={{ fontSize: '12px', color: '#706e6b' }}>Lifetime Value</span>
        <span style={{ fontSize: '12px', fontWeight: '600', color: '#080707' }}>$8.2M</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
        <span style={{ fontSize: '12px', color: '#706e6b' }}>Years as Customer</span>
        <span style={{ fontSize: '12px', fontWeight: '600', color: '#080707' }}>4.5</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
        <span style={{ fontSize: '12px', color: '#706e6b' }}>Products Owned</span>
        <span style={{ fontSize: '12px', fontWeight: '600', color: '#080707' }}>3</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span style={{ fontSize: '12px', color: '#706e6b' }}>Last Interaction</span>
        <span style={{ fontSize: '12px', fontWeight: '600', color: '#080707' }}>Today</span>
      </div>
    </div>
  </ComponentCard>
);

// ---------------------------------------------------------------------------
// Center Column - Record Detail
// ---------------------------------------------------------------------------
const RecordDetail = () => (
  <ComponentCard title="Account Information" label="RECORD DETAIL">
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
      <FieldDisplay label="Account Name" value="Acme Corporation" isLink />
      <FieldDisplay label="Industry" value="Technology" />
      <FieldDisplay label="Phone" value="(415) 555-1234" />
      <FieldDisplay label="Website" value="www.acmecorporation.com" isLink />
      <div style={{ marginBottom: '16px' }}>
        <div style={fieldLabelStyle}>Account Owner</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
          <Avatar
            variant="user"
            label="Emily Davis"
            size="small"
          />
          <span style={{ fontSize: '14px', color: '#0176d3', cursor: 'pointer' }}>
            Emily Davis
          </span>
        </div>
      </div>
      <FieldDisplay label="Rating" value="Hot" />
      <div style={{ gridColumn: '1 / -1', marginBottom: '16px' }}>
        <div style={fieldLabelStyle}>Description</div>
        <div style={{ ...fieldValueStyle, lineHeight: '1.5' }}>
          Acme Corporation is a leading enterprise technology company specializing in cloud infrastructure and analytics solutions. They have been a strategic customer since 2021, with a growing footprint across multiple product lines.
        </div>
      </div>
      <div style={{ gridColumn: '1 / -1' }}>
        <div style={fieldLabelStyle}>Billing Address</div>
        <div style={fieldValueStyle}>
          123 Market Street, Suite 400<br />
          San Francisco, CA 94105<br />
          United States
        </div>
      </div>
    </div>
  </ComponentCard>
);

// ---------------------------------------------------------------------------
// Center Column - Related Lists
// ---------------------------------------------------------------------------
const RelatedLists = () => {
  const [activeRelatedTab, setActiveRelatedTab] = useState(0);

  return (
    <ComponentCard title="Related Records" label="RELATED LISTS">
      <Tabs
        id="related-lists-tabs"
        selectedIndex={activeRelatedTab}
        onSelect={(index) => setActiveRelatedTab(index)}
      >
        <TabsPanel label="Opportunities (5)">
          <div style={{ marginTop: '8px' }}>
            <DataTable
              items={opportunitiesData}
              id="opportunities-table"
              fixedLayout
            >
              <DataTableColumn label="Opportunity Name" property="name" width="280px">
                <NameLinkCell />
              </DataTableColumn>
              <DataTableColumn label="Amount" property="amount" width="120px">
                <AmountCell />
              </DataTableColumn>
              <DataTableColumn label="Stage" property="stage" width="130px">
                <StageCell />
              </DataTableColumn>
              <DataTableColumn label="Close Date" property="closeDate" width="120px" />
            </DataTable>
            <div style={{ textAlign: 'center', marginTop: '12px' }}>
              <Button label="View All Opportunities" variant="base" />
            </div>
          </div>
        </TabsPanel>

        <TabsPanel label="Contacts (4)">
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '12px',
            marginTop: '8px',
          }}>
            {contactsData.map((contact) => (
              <ContactCard
                key={contact.id}
                name={contact.name}
                title={contact.title}
                email={contact.email}
              />
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: '12px' }}>
            <Button label="View All Contacts" variant="base" />
          </div>
        </TabsPanel>

        <TabsPanel label="Cases (5)">
          <div style={{ marginTop: '8px' }}>
            {casesData.map((caseItem) => (
              <div
                key={caseItem.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 0',
                  borderBottom: '1px solid #f3f3f3',
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <span style={{ fontSize: '13px', color: '#706e6b', fontWeight: '500' }}>
                      #{caseItem.number}
                    </span>
                    <StatusBadge status={caseItem.status} />
                  </div>
                  <div style={{ fontSize: '14px', color: '#0176d3', cursor: 'pointer', fontWeight: '500' }}>
                    {caseItem.subject}
                  </div>
                </div>
                <div style={{ flexShrink: 0, marginLeft: '16px' }}>
                  <StatusBadge status={caseItem.priority} />
                </div>
              </div>
            ))}
            <div style={{ textAlign: 'center', marginTop: '12px' }}>
              <Button label="View All Cases" variant="base" />
            </div>
          </div>
        </TabsPanel>
      </Tabs>
    </ComponentCard>
  );
};

// ---------------------------------------------------------------------------
// Center Column - Activity Timeline
// ---------------------------------------------------------------------------
const ActivityTimeline = () => {
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <ComponentCard title="Recent Activity" label="ACTIVITY TIMELINE">
      <ExpandableSection
        title="Activity Timeline"
        isOpen={isExpanded}
        onToggleOpen={() => setIsExpanded(!isExpanded)}
      >
        <div style={{ padding: '8px 0' }}>
          {timelineActivities.map((activity, idx) => (
            <TimelineItem
              key={idx}
              icon={activity.icon}
              iconColor={activity.iconColor}
              iconBg={activity.iconBg}
              title={activity.title}
              description={activity.description}
              timestamp={activity.timestamp}
              isLast={idx === timelineActivities.length - 1}
            />
          ))}
        </div>
      </ExpandableSection>
      <div style={{ textAlign: 'center', marginTop: '8px', borderTop: '1px solid #f3f3f3', paddingTop: '12px' }}>
        <Button label="View All Activity" variant="base" />
      </div>
    </ComponentCard>
  );
};

// ---------------------------------------------------------------------------
// Right Column - AI Insights
// ---------------------------------------------------------------------------
const AIInsights = () => (
  <ComponentCard title="Einstein Insights" label="AI INSIGHTS">
    <div style={{ marginBottom: '12px' }}>
      <ScopedNotification theme="info">
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
          <Icon category="utility" name="trending" size="x-small" style={{ fill: '#0176d3', flexShrink: 0, marginTop: '2px' }} />
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#080707', marginBottom: '4px' }}>
              Engagement Up 23%
            </div>
            <div style={{ fontSize: '12px', color: '#3e3e3c' }}>
              This account shows 23% higher engagement this quarter compared to last quarter. Product usage is up across all modules.
            </div>
          </div>
        </div>
      </ScopedNotification>
    </div>
    <div style={{ marginBottom: '12px' }}>
      <ScopedNotification theme="warning">
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
          <Icon category="utility" name="warning" size="x-small" style={{ fill: '#fe9339', flexShrink: 0, marginTop: '2px' }} />
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#080707', marginBottom: '4px' }}>
              Contacts Need Attention
            </div>
            <div style={{ fontSize: '12px', color: '#3e3e3c' }}>
              3 contacts haven't been contacted in 30+ days. Consider scheduling outreach to maintain relationship strength.
            </div>
          </div>
        </div>
      </ScopedNotification>
    </div>
    <div>
      <ScopedNotification theme="info">
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
          <Icon category="utility" name="einstein" size="x-small" style={{ fill: '#0176d3', flexShrink: 0, marginTop: '2px' }} />
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#080707', marginBottom: '4px' }}>
              Expansion Opportunity
            </div>
            <div style={{ fontSize: '12px', color: '#3e3e3c' }}>
              Based on usage patterns, this account is a strong candidate for the Analytics Add-on. Predicted close probability: 78%.
            </div>
          </div>
        </div>
      </ScopedNotification>
    </div>
  </ComponentCard>
);

// ---------------------------------------------------------------------------
// Right Column - Quick Actions
// ---------------------------------------------------------------------------
const QuickActions = () => (
  <ComponentCard title="Quick Actions" label="QUICK ACTIONS">
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
      <QuickActionButton icon="call" label="Log a Call" />
      <QuickActionButton icon="task" label="New Task" />
      <QuickActionButton icon="event" label="New Event" />
      <QuickActionButton icon="email" label="Send Email" />
      <QuickActionButton icon="note" label="New Note" />
      <QuickActionButton icon="opportunity" label="New Opportunity" />
    </div>
  </ComponentCard>
);

// ---------------------------------------------------------------------------
// Right Column - Key Metrics Charts
// ---------------------------------------------------------------------------
const KeyMetricsCharts = () => (
  <ComponentCard title="Performance Trends" label="KEY METRICS">
    {/* Revenue Trend */}
    <div style={{ marginBottom: '20px' }}>
      <div style={{ fontSize: '13px', fontWeight: '600', color: '#3e3e3c', marginBottom: '8px' }}>
        Revenue Trend (6 months)
      </div>
      <SLDSLineChart
        data={revenueChartData}
        height={160}
        options={{
          plugins: { legend: { display: false } },
          scales: {
            y: {
              ticks: {
                callback: function (value) {
                  return '$' + (value / 1000).toFixed(0) + 'k';
                },
              },
            },
          },
        }}
      />
    </div>

    {/* Product Mix */}
    <div>
      <div style={{ fontSize: '13px', fontWeight: '600', color: '#3e3e3c', marginBottom: '8px' }}>
        Product Mix
      </div>
      <SLDSDoughnutChart
        data={productMixData}
        height={180}
        options={{
          cutout: '55%',
          plugins: {
            legend: {
              position: 'bottom',
              labels: { font: { size: 11 }, padding: 12, boxWidth: 12 },
            },
          },
        }}
      />
    </div>
  </ComponentCard>
);

// ---------------------------------------------------------------------------
// Record Page Header
// ---------------------------------------------------------------------------
const RecordHeader = () => (
  <ComponentCard title="" label="RECORD PAGE HEADER">
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '8px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <div style={{
          width: '48px',
          height: '48px',
          borderRadius: '8px',
          backgroundColor: '#7F8DE1',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <Icon category="standard" name="account" size="medium" style={{ fill: 'white' }} />
        </div>
        <div>
          <div style={{ fontSize: '12px', color: '#706e6b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
            Account
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#080707', margin: 0 }}>
              Acme Corporation
            </h1>
            <span style={{
              backgroundColor: '#0176d3',
              color: 'white',
              padding: '2px 10px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: '600',
            }}>
              Enterprise Customer
            </span>
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
        <Button label="Edit" variant="neutral" />
        <Button label="Clone" variant="neutral" />
        <Button
          label="Delete"
          variant="neutral"
          iconCategory="utility"
          iconName="delete"
          iconPosition="left"
        />
        <Button label="Share" variant="neutral" iconCategory="utility" iconName="share" iconPosition="left" />
      </div>
    </div>

    {/* Stage Path */}
    <StagePath
      stages={['Lead', 'Prospect', 'Customer', 'Advocate']}
      currentStage="Customer"
    />
  </ComponentCard>
);

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
const RecordPageComposer = () => {
  const [layout, setLayout] = useState('Desktop');
  const [activeNav, setActiveNav] = useState('Record Home');

  const isDesktop = layout === 'Desktop';

  return (
    <ShowcasePageWrapper
      title="Record Page Composer"
      description="A fully-composed record page showing how components come together"
      icon="record"
      iconCategory="standard"
    >
      {/* Layout Toggle */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Icon category="utility" name="layout" size="small" style={{ fill: '#706e6b' }} />
          <span style={{ fontSize: '14px', fontWeight: '600', color: '#3e3e3c' }}>
            Page Layout
          </span>
        </div>
        <ButtonGroup id="layout-toggle">
          <Button
            label="Desktop"
            variant={layout === 'Desktop' ? 'brand' : 'neutral'}
            onClick={() => setLayout('Desktop')}
            iconCategory="utility"
            iconName="desktop"
            iconPosition="left"
          />
          <Button
            label="Tablet"
            variant={layout === 'Tablet' ? 'brand' : 'neutral'}
            onClick={() => setLayout('Tablet')}
            iconCategory="utility"
            iconName="tablet_portrait"
            iconPosition="left"
          />
        </ButtonGroup>
      </div>

      {/* Navigation Bar */}
      <NavBar activeTab={activeNav} onTabChange={setActiveNav} />

      {/* Record Page Header - Full Width */}
      <RecordHeader />

      {/* Three-Column Layout (Desktop) or Stacked (Tablet) */}
      {isDesktop ? (
        <div style={{
          display: 'grid',
          gridTemplateColumns: '25% 50% 25%',
          gap: '16px',
          alignItems: 'start',
        }}>
          {/* Left Column - Highlights Panel */}
          <div>
            <HighlightsPanel />
          </div>

          {/* Center Column - Detail & Activity */}
          <div>
            <RecordDetail />
            <RelatedLists />
            <ActivityTimeline />
          </div>

          {/* Right Column - Sidebar */}
          <div>
            <AIInsights />
            <QuickActions />
            <KeyMetricsCharts />
          </div>
        </div>
      ) : (
        <div>
          {/* Tablet: Center column full width, then left and right stack below */}
          <div style={{ marginBottom: '0' }}>
            <RecordDetail />
            <RelatedLists />
            <ActivityTimeline />
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '16px',
            alignItems: 'start',
          }}>
            <div>
              <HighlightsPanel />
              <QuickActions />
            </div>
            <div>
              <AIInsights />
              <KeyMetricsCharts />
            </div>
          </div>
        </div>
      )}

      {/* Page Footer */}
      <div style={{
        marginTop: '24px',
        padding: '16px 20px',
        backgroundColor: 'white',
        border: '1px solid #dddbda',
        borderRadius: '4px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Icon category="utility" name="info" size="x-small" style={{ fill: '#706e6b' }} />
          <span style={{ fontSize: '12px', color: '#706e6b' }}>
            This composed page demonstrates 8 individual component sections working together. Each blue-bordered card represents a separately buildable component.
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexShrink: 0 }}>
          <span style={{ fontSize: '12px', color: '#706e6b' }}>
            Last modified: Feb 12, 2026
          </span>
          <span style={{ fontSize: '12px', color: '#706e6b' }}>
            Components: 8
          </span>
        </div>
      </div>
    </ShowcasePageWrapper>
  );
};

export default RecordPageComposer;
